// ADR 3.10.26f §3 — the box an admitted visitor sees: the pinned doors as
// top-level icons, then the trigger and its panel.
//
// Closing reuses `ui/`'s two hooks rather than a third "closed": Escape
// returns focus to the trigger (`useEscapeCloses`), and an outside
// pointer-down or a route change clicks the expanded trigger
// (`useDismissMenu`). The pin toggles carry `aria-pressed`, never
// `aria-expanded`, so that hook cannot mistake one for the trigger.
//
// The pinned icons sit outside the wrapper, so a press on one is an outside
// pointer-down and closes an open panel on its way.

import { useCallback, useId, useRef, useState, type MutableRefObject, type RefCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { adminConsoleSwitched, headerEntryPinned } from '../../features/admin/analytics'
import { useCoachAnchor } from '../../features/coach/anchors'
import { TOUR_ANCHORS } from '../../features/coach/tours/tourFlows'
import { useDeveloperTools } from '../../features/connections/useDeveloperTools'
import { usePref } from '../../features/settings/prefStore'
import { IconButton } from '../../ui/controls'
import { useDismissMenu } from '../../ui/controls/useDismissMenu'
import { useEscapeCloses } from '../../ui/useEscapeCloses'
import { consoleFor } from '../nav/destinations'
import { activeEntry, switcherEntries, type SwitcherEntry, type SwitcherId } from './entries'
import { headerPins } from './pins'
import { SwitcherPanel } from './SwitcherPanel'

/** §4 — the entry id is not the event's `to` for Developer, so the mapping is spelled out. */
const SWITCHED_TO: Record<SwitcherId, Exclude<SwitchedTo, 'app'>> = {
  settings: 'settings',
  developer: 'admin',
  connections: 'connections',
}

type SwitchedTo = Parameters<typeof adminConsoleSwitched>[0]

/** The pinned, present doors, in display order; no current state (the panel's bold row is the one). */
function PinnedDoors({ entries, pins, onGo }: {
  entries: SwitcherEntry[]
  pins: SwitcherId[]
  onGo: (entry: SwitcherEntry) => void
}) {
  const { t } = useTranslation()
  return (
    <>
      {entries
        .filter((entry) => pins.includes(entry.id))
        .map((entry) => (
          <IconButton key={entry.id} icon={entry.icon} label={t(entry.labelKey)} onClick={() => onGo(entry)} />
        ))}
    </>
  )
}

/** Every door navigates the same way: §4's event, then the route. */
function useGo(): (path: string, to: SwitchedTo) => void {
  const navigate = useNavigate()
  return (path, to) => {
    adminConsoleSwitched(to)
    navigate(path)
  }
}

/** The box's open state and its two closing behaviours, wired to refs that exist from first mount. */
function useBoxOpen() {
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement | null>(null)
  const close = useCallback(() => setOpen(false), [])
  useEscapeCloses(wrapper, close, trigger)
  useDismissMenu(wrapper)
  // `useId` spells ids with colons; stripped as `Disclosure` does.
  const panelId = `${useId().replace(/:/g, '')}-switcher`
  return { open, setOpen, wrapper, trigger, triggerRef: useTriggerAnchor(trigger), panelId }
}

/**
 * ADR 17.10.26i §5 — the trigger is also the tour's `header.settings` target
 * (the gear's id, for an admitted visitor). The anchor's callback ref is
 * merged with `trigger`, which focus return still needs, never replaces it.
 */
function useTriggerAnchor(trigger: MutableRefObject<HTMLButtonElement | null>): RefCallback<HTMLButtonElement> {
  const anchor = useCoachAnchor<HTMLButtonElement>(TOUR_ANCHORS.headerSettings)
  return useCallback(
    (element: HTMLButtonElement | null) => {
      trigger.current = element
      anchor(element)
    },
    [trigger, anchor],
  )
}

export function SwitcherBox() {
  const { t } = useTranslation()
  const go = useGo()
  const { pathname } = useLocation()
  const { shown } = useDeveloperTools('header')
  const [pins, setPins] = usePref(headerPins)
  const { open, setOpen, wrapper, trigger, triggerRef, panelId } = useBoxOpen()
  const entries = switcherEntries({ admitted: true, developerShown: shown })

  // A row closes the box explicitly (so the current route's row closes it too)
  // and hands focus back to the trigger before it navigates.
  function choose(path: string, to: SwitchedTo) {
    setOpen(false)
    trigger.current?.focus()
    go(path, to)
  }

  function togglePin(id: SwitcherId) {
    const pinned = !pins.includes(id)
    setPins(pinned ? [...pins, id] : pins.filter((pin) => pin !== id))
    headerEntryPinned(id, pinned)
  }

  return (
    <>
      <PinnedDoors entries={entries} pins={pins} onGo={(entry) => go(entry.path, SWITCHED_TO[entry.id])} />
      <div ref={wrapper} className="relative">
        <IconButton
          ref={triggerRef}
          icon="expand_more"
          label={t('header_switcher_label')}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((was) => !was)}
        />
        <SwitcherPanel
          id={panelId}
          open={open}
          entries={entries}
          active={activeEntry(pathname, entries)}
          pins={pins}
          inConsole={consoleFor(pathname) === 'admin'}
          onChoose={(entry) => choose(entry.path, SWITCHED_TO[entry.id])}
          onBack={() => choose('/', 'app')}
          onTogglePin={togglePin}
        />
      </div>
    </>
  )
}
